import type { Task, WbsNode } from "./types";

const scopes: [string, string, string[]][] = [
  ["1.1", "PLC v3 & I/O", ["I/O hardware & AC500 conversion", "PLC software & connection checks", "PLC communication"]],
  ["1.2", "Drives", ["Warehouse preparation", "Drive installation & connections", "Drive software & parameters", "Drive communication verification"]],
  ["1.3", "Controls & remote desk", ["Desk V2 to V3 conversion", "Desk commissioning", "Belly box", "Upgraded desk verification"]],
  ["1.4", "Sensors & measurement", ["BROSA / load cells", "Inclinometer", "Lidar", "LPS / TPS"]],
  ["1.5", "Network, video & interfaces", ["Network infrastructure & connections", "Video verification", "XCC interface verification"]],
  ["1.6", "Safety functions", ["Gate lock", "Emergency stop & buttons"]],
  ["1.7", "Crane functions & integrated testing", ["Crane startup & basic motions", "Spreader & twist locks", "Synchronisation & full speed", "Function verification & sway/skew control", "Container full path", "Automatic motions"]],
  ["1.8", "Shared installation", ["E-room access", "Shared cabling", "Electrical installation verification", "Mechanical installation verification"]],
  ["1.9", "Changeover logistics & test support", ["Crane & portal trolley positioning", "Worksite & cherry picker", "Operator & IT support", "Test weight, AGV & container"]],
];

export const DEFAULT_WBS: WbsNode[] = [
  { id: "wbs-1", code: "1", name: "Crane Control Upgrade", parentId: null },
  ...scopes.flatMap(([code, name, packages]) => [
    { id: `wbs-${code}`, code, name, parentId: "wbs-1" },
    ...packages.map((name, i) => ({ id: `wbs-${code}.${i + 1}`, code: `${code}.${i + 1}`, name, parentId: `wbs-${code}` })),
  ]),
];

const assignments: Record<string, string[]> = {
  "1.1.2": ["C1"], "1.1.3": ["C2", "C5"],
  "1.2.1": ["L1"], "1.2.3": ["C8"], "1.2.4": ["C9", "M3"],
  "1.3.1": ["I11"], "1.3.2": ["C3", "C18"], "1.3.3": ["C4"], "1.3.4": ["C26"],
  "1.4.1": ["I3", "I6", "C17"], "1.4.2": ["I8", "I10"], "1.4.3": ["I9"],
  "1.5.2": ["C19"], "1.5.3": ["C20"], "1.6.1": ["C6"], "1.6.2": ["C7"],
  "1.7.1": ["C10", "C11", "C12"], "1.7.2": ["C13", "C16"], "1.7.3": ["C14", "C15"],
  "1.7.4": ["C23", "C24"], "1.7.5": ["C25"], "1.7.6": ["C27", "C28"],
  "1.8.1": ["I1"], "1.8.2": ["I2"], "1.8.3": ["I12"], "1.8.4": ["I13"],
  "1.9.1": ["L2", "L5"], "1.9.2": ["L3", "L4"], "1.9.3": ["L6", "N1"], "1.9.4": ["L7", "L8", "L9"],
};
const seedAssignments = new Map(Object.entries(assignments).flatMap(([code, ids]) => ids.map((id) => [id, `wbs-${code}`] as const)));

// Undefined denotes pre-WBS data. Explicit null means intentionally unassigned.
export function migrateTaskWbs(t: Task): Task {
  return t.wbsId === undefined ? { ...t, wbsId: seedAssignments.get(t.id) ?? null } : t;
}
export function nextFreeWbsCode(nodes: WbsNode[], parentCode: string): string {
  let n = 1;
  while (nodes.some((node) => node.code === `${parentCode}.${n}`)) n++;
  return `${parentCode}.${n}`;
}
export function workPackages(nodes: WbsNode[]): WbsNode[] {
  return nodes.filter((n) => n.parentId !== null && n.parentId !== "wbs-1" && !nodes.some((child) => child.parentId === n.id))
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
}
export function wbsLabel(t: Pick<Task, "wbsId">, nodes: WbsNode[] = DEFAULT_WBS): string {
  return nodes.find((n) => n.id === t.wbsId)?.code ?? "WBS to assign";
}
export function wbsSearchText(t: Task, nodes: WbsNode[]): string {
  let node = nodes.find((n) => n.id === t.wbsId);
  const parts: string[] = [];
  const visited = new Set<string>();
  while (node && !visited.has(node.id)) {
    visited.add(node.id);
    parts.push(node.code, node.name);
    node = nodes.find((n) => n.id === node!.parentId);
  }
  return parts.length ? parts.join(" ") : "WBS to assign";
}
